<template>
  <div>
    <Card cardClasses="border-0 shadow-sm mb-5">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0 text-primary">
          <vue-feather type="user" size="20" class="me-2"></vue-feather>
          Casual Details
        </h4>
        <div class="d-flex gap-2">
          <button class="btn btn-light btn-sm d-flex align-items-center shadow-sm" @click="$router.go(-1)">
            <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
            Back
          </button>
          <button v-if="permissions.includes('edit casuals')" class="btn btn-primary btn-sm d-flex align-items-center shadow-sm" @click="editCasual">
            <vue-feather type="edit" size="16" class="me-1"></vue-feather>
            Edit Casual
          </button>
        </div>
      </div>
      <div class="row">
        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
        </div>
        <div v-if="!loading" class="employee-profile mb-4">
          <div class="row align-items-center">
            <div class="col-md-3 col-lg-2 text-center mb-3 mb-md-0">
              <div class="position-relative d-inline-block">
                <img class="rounded-circle img-thumbnail shadow-sm" style="width: 150px; height: 150px; object-fit: cover;" :src="avatar" alt="">
                <button class="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle p-2" @click="$refs.refInputEl.click()" title="Upload Photo">
                  <vue-feather type="camera" size="16"></vue-feather>
                </button>
                <input
                    ref="refInputEl"
                    type="file"
                    name="logo"
                    accept=".jpeg,.png,.jpg"
                    hidden
                    @input="changeAvatar"
                >
              </div>
            </div>
            <div class="col-md-9 col-lg-10">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                <h2 class="mb-0">{{ name }}</h2>
                <div>
                  <badge class="ms-2 badge-lg" :type="getPillByStatus(casual.status)">
                    <vue-feather :type="getIconByStatus(casual.status)" size="14" class="me-1"></vue-feather>
                    {{ casual.status }}
                  </badge>
                </div>
              </div>
              <div class="row g-3">
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="hash" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Casual No</div>
                      <div class="fw-medium">{{ casual.casual_no }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="layers" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Department</div>
                      <div class="fw-medium">{{ casual.department.name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="map-pin" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Work Station</div>
                      <div class="fw-medium">{{ casual.station.name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="briefcase" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Duty</div>
                      <div class="fw-medium">{{ casual.duty }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="border-1 border-light border-opacity-75 my-3" />

        <Nav nav-fill :init-active-tab="1" class="employee-tabs">
          <NavItem title="Details">
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-if="!loading" class="pt-3">
              <div class="row">
                <div class="col-12 mb-4">
                  <div class="card border-0 bg-light rounded-3">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="user" size="18" class="text-primary me-2"></vue-feather>
                        Personal Information
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">ID Number</div>
                            <div class="fw-medium">{{ casual.id_number }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Gender</div>
                            <div class="fw-medium">{{ casual.gender }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Date of Birth</div>
                            <div class="fw-medium">{{ formatDate(casual.dob) }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Phone</div>
                            <div class="fw-medium">{{ casual.phone }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Address</div>
                            <div class="fw-medium">{{ casual.address }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 mb-4">
                  <div class="card border-0 bg-light rounded-3">
                    <div class="card-body">
                      <h5 class="card-title mb-3 d-flex align-items-center">
                        <vue-feather type="briefcase" size="18" class="text-primary me-2"></vue-feather>
                        Employment Details
                      </h5>
                      <div class="row g-3">
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Employment Date</div>
                            <div class="fw-medium">{{ formatDate(casual.employment_date) }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Daily Pay</div>
                            <div class="fw-medium">{{ Ksh(casual.pay) }}</div>
                          </div>
                        </div>
                        <div class="col-md-4 col-lg-3">
                          <div class="detail-item">
                            <div class="text-muted small">Termination Date</div>
                            <div class="fw-medium">{{ !casual.termination_date ? 'N/A' : formatDate(casual.termination_date) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </NavItem>
          <NavItem title="Advance">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="dollar-sign" size="18" class="text-primary me-2"></vue-feather>
                  Casual Advances
                </h5>
                <button class="btn btn-primary btn-sm" @click="advanceModal = true; advanceMode = 'add'">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Advance
                </button>
              </div>

              <div v-if="advances.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="dollar-sign" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No advances found</h5>
                <p class="text-muted small mb-0">Add an advance to see it here</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Amount</th>
                      <th scope="col" class="border-0">Description</th>
                      <th scope="col" class="border-0">Created</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="advance in advances" :key="advance.id">
                      <td class="align-middle">{{ advance.name }}</td>
                      <td class="align-middle">{{ Ksh(advance.amount) }}</td>
                      <td class="align-middle">
                        <span v-if="advance.description" class="text-truncate d-inline-block" style="max-width: 150px;" :title="advance.description">
                          {{ advance.description }}
                        </span>
                        <span v-else class="text-muted fst-italic">—</span>
                      </td>
                      <td class="align-middle">{{ formatDate(advance.created_at) }}</td>
                      <td class="align-middle text-end">
                        <button
                          class="btn btn-outline-primary btn-sm me-1"
                          @click="advanceModal = true; advanceMode = 'edit'; populateAdvanceEdit(advance)"
                        >
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button
                          class="btn btn-outline-danger btn-sm"
                          @click="confirmDialog('Advance', () => deleteAdvance(advance.id))"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </NavItem>
          <NavItem title="Deductions">
            <div class="pt-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 d-flex align-items-center">
                  <vue-feather type="trending-down" size="18" class="text-primary me-2"></vue-feather>
                  Casual Deductions
                </h5>
                <button class="btn btn-primary btn-sm" @click="deductionModal = true; deductionMode = 'add'">
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Deduction
                </button>
              </div>

              <div v-if="deductions.length === 0" class="text-center py-5 bg-light rounded-3">
                <vue-feather type="trending-down" size="48" class="text-muted mb-3"></vue-feather>
                <h5 class="text-muted">No deductions found</h5>
                <p class="text-muted small mb-0">Add a deduction to see it here</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover border-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="border-0">Name</th>
                      <th scope="col" class="border-0">Amount</th>
                      <th scope="col" class="border-0">From</th>
                      <th scope="col" class="border-0">To</th>
                      <th scope="col" class="border-0">Description</th>
                      <th scope="col" class="border-0">Created</th>
                      <th scope="col" class="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="deduction in deductions" :key="deduction.id">
                      <td class="align-middle">{{ deduction.name }}</td>
                      <td class="align-middle">{{ Ksh(deduction.amount) }}</td>
                      <td class="align-middle">{{ formatDate(deduction.from) }}</td>
                      <td class="align-middle">{{ formatDate(deduction.to) }}</td>
                      <td class="align-middle">
                        <span v-if="deduction.description" class="text-truncate d-inline-block" style="max-width: 150px;" :title="deduction.description">
                          {{ deduction.description }}
                        </span>
                        <span v-else class="text-muted fst-italic">—</span>
                      </td>
                      <td class="align-middle">{{ formatDate(deduction.created_at) }}</td>
                      <td class="align-middle text-end">
                        <button
                          class="btn btn-outline-primary btn-sm me-1"
                          @click="deductionModal = true; deductionMode = 'edit'; populateDeductionEdit(deduction)"
                        >
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button
                          class="btn btn-outline-danger btn-sm"
                          @click="confirmDialog('Deduction', () => deleteDeduction(deduction.id))"
                        >
                          <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </NavItem>
        </Nav>
      </div>
    </Card>

    <!-- Add Advance Modal -->
    <Modal :open="advanceModal" @closed="advanceModal = false">
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          <vue-feather type="dollar-sign" size="18" class="text-primary me-2"></vue-feather>
          <span v-if="advanceMode === 'add'">Add Advance</span>
          <span v-else>Edit Advance</span>
        </h5>
        <button @click="advanceModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-12">
              <div class="form-floating">
                <input type="text" class="form-control" id="advanceName" v-model="advance.name" placeholder="Name">
                <label for="advanceName">Name</label>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-floating">
                <input type="number" class="form-control" id="advanceAmount" v-model="advance.amount" placeholder="Amount">
                <label for="advanceAmount">Amount (daily advance)</label>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-floating">
                <input type="text" class="form-control" id="advanceDescription" v-model="advance.description" placeholder="Description (optional)">
                <label for="advanceDescription">Description (optional)</label>
              </div>
            </div>
            <div class="col-md-12">
              <div class="alert alert-info d-flex">
                <vue-feather type="info" size="24" class="text-primary me-3 mt-1"></vue-feather>
                <div>
                  <h6 class="alert-heading fw-bold">Advance Details</h6>
                  <p class="mb-0">Ksh. {{ advance.amount || 0 }} added daily to Casual's pay.</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-0">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="advanceModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="advanceMode === 'add'" @click="addAdvance($event)">
          <vue-feather type="plus" size="16" class="me-1"></vue-feather>
          Add Advance
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateAdvance($event, advance.id)">
          <vue-feather type="save" size="16" class="me-1"></vue-feather>
          Update Advance
        </button>
      </div>
    </Modal>
    <!-- Add Advance Modal End -->

    <!-- Add Deduction Modal -->
    <Modal :open="deductionModal" @closed="deductionModal = false" dialog-size="modal-lg">
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          <vue-feather type="trending-down" size="18" class="text-primary me-2"></vue-feather>
          <span v-if="deductionMode === 'add'">Add Deduction</span>
          <span v-else>Edit Deduction</span>
        </h5>
        <button @click="deductionModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-floating">
                <input type="text" class="form-control" id="deductionName" v-model="deduction.name" placeholder="Name">
                <label for="deductionName">Name</label>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-floating">
                <input type="number" class="form-control" id="deductionAmount" v-model="deduction.amount" placeholder="Amount">
                <label for="deductionAmount">Amount (daily deduction)</label>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-floating">
                <input type="date" class="form-control" id="deductionFrom" v-model="deduction.from" placeholder="From">
                <label for="deductionFrom">From</label>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-floating">
                <input type="date" class="form-control" id="deductionTo" v-model="deduction.to" placeholder="To">
                <label for="deductionTo">To</label>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-floating">
                <input type="text" class="form-control" id="deductionDescription" v-model="deduction.description" placeholder="Description (optional)">
                <label for="deductionDescription">Description (optional)</label>
              </div>
            </div>
            <div class="col-md-12">
              <div class="alert alert-info d-flex">
                <vue-feather type="info" size="24" class="text-primary me-3 mt-1"></vue-feather>
                <div>
                  <h6 class="alert-heading fw-bold">Deduction Details</h6>
                  <p class="mb-0">Ksh. {{ deduction.amount || 0 }} deducted daily for a period of <strong>{{ deductionDays || 0 }}</strong> days, totaling Ksh. <strong>{{ (deductionDays || 0) * (deduction.amount || 0) }}</strong></p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-0">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="deductionModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="deductionMode === 'add'" @click="addDeduction($event)">
          <vue-feather type="plus" size="16" class="me-1"></vue-feather>
          Add Deduction
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateDeduction($event, deduction.id)">
          <vue-feather type="save" size="16" class="me-1"></vue-feather>
          Update Deduction
        </button>
      </div>
    </Modal>
    <!-- Add Deduction Modal End -->

  </div>
</template>

<script>
import casualService from "@/api/casualService";
import formatDate from "../../utils/formatDate";
import Ksh from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import confirmDialog from "@/utils/confirmDialog";
import advanceService from "@/api/casualAdvanceService";
import deductionService from "@/api/casualDeductionService";
import loadingButton from "@/utils/loadingButton";

export default {
  name: "CasualDetails",
  data() {
    return {
      Ksh,
      profile: {},
      casual: {},
      avatarUrl: process.env.VUE_APP_API_IMAGE_URL + 'casual/',
      loading: true,
      advances: [],
      deductions: [],
      advanceMode: 'add',
      advanceModal:false,
      permissions: [],
      advance: {
        casual_id: this.$route.params.id,
        name: '',
        amount: '',
        description: '',
      },
      deductionMode: 'add',
      deductionModal:false,
      deduction: {
        casual_id: this.$route.params.id,
        name: '',
        amount: '',
        from: '',
        to: '',
        description: '',
      },
      deductionDays: 0
    }
  },
  methods: {
    formatDate,
    formatNumber,
    confirmDialog,
    async getCasual() {
      this.casual = await casualService.getCasual(this.$route.params.id)
      this.loading = false
    },
    editCasual() {
      this.$router.push('/edit-casual/' + this.$route.params.id);
    },
    async getAdvances() {
      this.advances = await advanceService.getAdvances(this.$route.params.id)
    },
    async getDeductions() {
      this.deductions = await deductionService.getDeductions(this.$route.params.id)
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
        case 'suspended':
          return 'warning';
        case 'terminated':
          return 'dark';
      }
    },
    getIconByStatus(status) {
      switch (status) {
        case 'active':
          return 'check-circle';
        case 'inactive':
          return 'x-circle';
        case 'suspended':
          return 'alert-circle';
        case 'terminated':
          return 'slash';
        default:
          return 'help-circle';
      }
    },
    changeAvatar : async function(file) {
      let fileReader = this.processFile(file)
      fileReader.onload = async () => {
        if (typeof fileReader.result === 'string')
          await casualService.updateAvatar({
            file: fileReader.result,
            casual_id: this.$route.params.id
          })
      }
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
    async addAdvance(e) {
      loadingButton(e)
      let response = await advanceService.createAdvance(this.advance)
      if (response)
        this.advanceModal = false
        await this.getAdvances()
      loadingButton(e, false)
    },
    async updateAdvance(e, id) {
      loadingButton(e)
      let response = await advanceService.updateAdvance(this.advance, id)
      if (response)
        this.advanceModal = false
        await this.getAdvances()
      loadingButton(e, false)
    },
    async deleteAdvance(id) {
      await advanceService.deleteAdvance(id)
      await this.getAdvances()
    },
    populateAdvanceEdit(advance) {
      this.advance = advance
    },
    async addDeduction(e) {
      loadingButton(e)
      let response = await deductionService.createDeduction(this.deduction)
      if (response)
        this.deductionModal = false
        await this.getDeductions()
      loadingButton(e, false)
    },
    async updateDeduction(e, id) {
      loadingButton(e)
      let response = await deductionService.updateDeduction(this.deduction, id)
      if (response)
        this.deductionModal = false
        await this.getDeductions()
      loadingButton(e, false)
    },
    async deleteDeduction(id) {
      await deductionService.deleteDeduction(id)
      await this.getDeductions()
    },
    populateDeductionEdit(deduction) {
      this.deduction = deduction
    },
    calculateDays() {
      let from = new Date(this.deduction.from)
      let to = new Date(this.deduction.to)
      this.deductionDays = (to - from) / (1000 * 60 * 60 * 24) + 1
    }
  },

  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.permissions = this.profile.permissionNames
    await this.getCasual()
    await this.getAdvances()
    await this.getDeductions()
  },

  computed: {
    name() {
      let middle_name = this.casual.middle_name === null ? ' ' : this.casual.middle_name
      return this.casual.first_name + " " + middle_name + " " + this.casual.last_name
    },
    avatar() {
      return this.casual.avatar !== null ? this.avatarUrl + this.casual.avatar : 'https://villagesonmacarthur.com/wp-content/uploads/2020/12/Blank-Avatar.png'
    }
  },

  watch: {
    advanceModal: function () {
      if (!this.advanceModal) {
        this.advance = {
          casual_id: this.$route.params.id,
        }
      }
    },
    deductionModal: function () {
      if (!this.deductionModal) {
        this.deduction = {
          casual_id: this.$route.params.id,
        }
      }
    },
    'deduction.from': function () {
      this.calculateDays()
    },
    'deduction.to': function () {
      this.calculateDays()
    },
    'deduction.amount': function () {
      this.calculateDays()
    }
  }
}
</script>
